// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import constants from "@store/constants";
import Icon, { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { focusStyles } from "@toolkit/Themes/common/mixins/common";
import React, { FC } from "react";
import { css } from "styled-components";
import ProfileAdd from "@store/Library/Components/Icons/ProfileAdd";

export interface CollapsableInviteYourColleaguePresentationProps
    extends ExpandStyles<CollapsableInviteYourColleagueStyles> {}

export interface CollapsableInviteYourColleagueProps extends CollapsableInviteYourColleaguePresentationProps {
    isCollapsed?: boolean;
}

export interface CollapsableInviteYourColleagueStyles {
    wrapper?: InjectableCss;
    uncollapsedWrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    collapsedLink?: ClickablePresentationProps;
    collapsedLinkIcon?: IconPresentationProps;
    collapsedLinkTooltip?: TooltipPresentationProps;
}

export const collapsableInviteYourColleagueStyles: CollapsableInviteYourColleagueStyles = {
    uncollapsedWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};
            padding: ${pxToRem(16)};
            border-radius: ${pxToRem(8)};
            background-color: ${getColor("tertiary")};
            background-image: linear-gradient(90deg, ${getColor("tertiary")}, ${getColor("tertiary.light")});
        `,
    },
    text: {
        forwardAs: "p",
        css: css`
            color: ${getColor("invert")};
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    button: {
        variant: "inverted" as any,
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            width: 100%;
        `,
    },
    collapsedLink: {
        css: css`
            ${focusStyles()}
            position: relative;
            isolation: isolate;
            display: flex;
            align-items: center;
            justify-content: center;
            width: ${pxToRem(48)};
            height: ${pxToRem(48)};
            border-radius: ${pxToRem(8)};
            overflow: hidden;
            color: ${getColor("common.background")};
            background-color: ${getColor("tertiary")};

            &::before {
                content: "";
                position: absolute;
                inset: 0;
                z-index: -1;
                background-image: linear-gradient(90deg, ${getColor("tertiary")}, ${getColor("tertiary.light")});
                opacity: 1;
                transition: ${transitionHoverStyles("opacity")};
            }

            &:hover::before,
            &:active::before {
                opacity: 0;
            }
        `,
    },
    collapsedLinkIcon: {
        color: "inherit",
        size: 28,
        src: ProfileAdd,
    },
    collapsedLinkTooltip: {
        direction: "right",
        offset: 4,
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: ${pxToRem(0, 4)};
            `,
            tooltipBody: css`
                padding: ${pxToRem(8, 16)};
            `,
        },
    },
};

const CollapsableInviteYourColleague: FC<CollapsableInviteYourColleagueProps> = ({
    extendedStyles,
    isCollapsed,
    overridedStyles,
}) => {
    const styles = useExpandProps(collapsableInviteYourColleagueStyles, extendedStyles, overridedStyles);
    const pageLink = usePageLinkByPageType(constants.pageType.inviteYourColleague);

    return (
        <StyledWrapper {...styles.wrapper}>
            {isCollapsed ? (
                <Tooltip
                    {...styles.collapsedLinkTooltip}
                    displayOnHover
                    text={translate("Invite Colleague")}
                    triggerIsClickable
                    triggerComponent={
                        <Clickable {...styles.collapsedLink} href={pageLink?.url}>
                            <Icon {...styles.collapsedLinkIcon} />
                            <VisuallyHidden>{translate("Invite Colleague")}</VisuallyHidden>
                        </Clickable>
                    }
                />
            ) : (
                <StyledWrapper {...styles.uncollapsedWrapper}>
                    <Typography {...styles.text}>
                        {translate("Bring a colleague onboard. Share the benefits.")}
                    </Typography>
                    <Button {...styles.button} href={pageLink?.url}>
                        {translate("Invite Colleague")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default CollapsableInviteYourColleague;
